<template>
  <div v-if="loading">
    <spinner />
  </div>
  <div v-else class="container">
    <subscription-plan-list
      v-if="activeStep === 1"
      :initial-values="subscriptionPlans"
      @createNewPlan="activeStep = 2"
      @editPlan="handleEdit($event)"
      @delete="handleDelete"
    />
    <create-or-edit-subscription-plan
      v-if="activeStep === 2"
      :event-templates="organizationEventTemplates"
      :interests="organizationInterests"
      :initial-value="initialValue"
      @cancel="handleReset"
      @create="handleCreate"
      @update="handleUpdate"
    />
    <simple-snackbar
      :open.sync="snackbar.open"
      :message-prop="organizationText(snackbar.message)"
      :color="snackbar.color"
    />
  </div>
</template>

<script>
import OrganizationLocalization from '@/components/organizations/organization-all-display'

import SubscriptionPlanList from '@/components/organizations/subscriptions/SubscriptionPlanList.vue'
import CreateOrEditSubscriptionPlan from '@/components/organizations/subscriptions/CreateOrEditSubscriptionPlan.vue'
import {
  GET_ORGANIZATION_PREPAID_COUPON_AND_INTEREST_AND_EVENT_TEMPLATE,
  CREATE_ORGANIZATION_PREPAID_COUPON,
  UPDATE_ORGANIZATION_PREPAID_COUPON,
  DELETE_ORGANIZATION_PREPAID_COUPON,
} from '../../../graphql/prepaidCoupon.graphql'
import Spinner from './../../shared/spinner.vue'
import SimpleSnackbar from '@/components/shared/snackbars/simple-snackbar.vue'
import { omit } from 'lodash'
import { v4 as uuid } from 'uuid'

export default {
  mixins: [OrganizationLocalization],
  name: 'subscriptions-plan-container',
  components: {
    SubscriptionPlanList,
    CreateOrEditSubscriptionPlan,
    SimpleSnackbar,
    Spinner,
  },
  props: {
    organizationSlug: {
      type: String,
    },
  },
  data() {
    return {
      activeStep: 1,
      subscriptionPlans: [],
      loading: true,
      organizationEventTemplates: [],
      organizationInterests: [],
      initialValue: {},
      snackbar: {
        open: false,
        message: '',
        color: 'primary',
      },
    }
  },
  async mounted() {
    await this.fetchPrepaidCouponsAndEventTemplatesAndOrganizationInterests()
  },
  methods: {
    async handleCreate(info) {
      const couponItems = this.modifyCouponItemData(info.couponItems)
      const { errors } = await this.$apollo.mutate({
        mutation: CREATE_ORGANIZATION_PREPAID_COUPON,
        variables: {
          organizationSlug: this.organizationSlug,
          prepaidCouponInput: {
            ...info,
            couponItems,
          },
        },
      })
      await this.showSnackbarMsgAndRefetchData(errors, 'Plan created')
    },
    modifyCouponItemData(couponItems) {
      return couponItems.map(item => ({
        ...omit(item, ['externalId']),
        genreIds: item.genreIds ? item.genreIds.map(g => parseInt(g)) : undefined,
        tagIds: item.tagIds ? item.tagIds.map(t => parseInt(t)) : undefined,
        type: item.type.toUpperCase(),
      }))
    },
    async handleUpdate(info) {
      const couponItems = this.modifyCouponItemData(info.couponItems)
      const { currency, __typename, ...prepaidCouponInput } = info
      const { errors } = await this.$apollo.mutate({
        mutation: UPDATE_ORGANIZATION_PREPAID_COUPON,
        variables: {
          organizationSlug: this.organizationSlug,
          prepaidCouponInput: {
            ...prepaidCouponInput,
            couponItems,
          },
        },
      })
      await this.showSnackbarMsgAndRefetchData(errors, 'Plan updated')
    },
    async handleDelete(selected) {
      const { errors } = await this.$apollo.mutate({
        mutation: DELETE_ORGANIZATION_PREPAID_COUPON,
        variables: {
          organizationSlug: this.organizationSlug,
          prepaidCouponIds: selected.map(s => parseInt(s)),
        },
      })
      this.activeStep = 0
      await this.showSnackbarMsgAndRefetchData(errors, 'Plan deleted')
    },
    async handleRefetchData() {
      await this.fetchPrepaidCouponsAndEventTemplatesAndOrganizationInterests()
      this.handleReset()
    },
    async showSnackbarMsgAndRefetchData(errors, msg) {
      if (errors) {
        this.snackbar = {
          open: true,
          message: 'Oops, something went wrong!',
          color: 'error',
        }
      } else {
        this.snackbar = {
          open: true,
          message: this.organizationMessage('organizationAll.value0_successfully_4bf9e6b5', '{{value0}} successfully!', { value0: msg }),
        }
        await this.handleRefetchData()
      }
    },
    handleEdit(selectedPlanId) {
      this.initialValue = this.subscriptionPlans.find(plan => plan.id === selectedPlanId)
      this.activeStep = 2
    },
    handleReset() {
      this.activeStep = 1
      this.initialValue = {}
    },
    async fetchPrepaidCouponsAndEventTemplatesAndOrganizationInterests() {
      const { data, errors } = await this.$apollo.query({
        query: GET_ORGANIZATION_PREPAID_COUPON_AND_INTEREST_AND_EVENT_TEMPLATE,
        variables: {
          organizationSlug: this.organizationSlug,
        },
      })
      if (errors) {
        await this.showSnackbarMsgAndRefetchData(errors)
      } else {
        this.subscriptionPlans = data.getOrganizationPrepaidCoupons.map(coupon => ({
          ...coupon,
          couponItems: coupon.couponItems.map(item => ({
            ...omit(item, ['genres', 'tags', '__typename']),
            externalId: uuid(),
            genreIds: item.genres.reduce((arr, curr) => [...arr, parseInt(curr.id)], []),
            tagIds: item.tags.reduce((arr, curr) => [...arr, parseInt(curr.id)], []),
          })),
        }))
        this.organizationEventTemplates = data.getOrganizationEventTemplates
        this.organizationInterests = data.getOrganizationInterest.map(interest => ({
          ...interest,
          id: parseInt(interest.id),
        }))
        this.loading = false
      }
    },
  },
}
</script>

<style scoped>
.container {
  padding: 0 !important;
  margin: 0 !important;
  display: flex;
  flex-direction: column;
  max-width: 43.75rem;
}
</style>
